import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Themenfeld } from '../../model/themenfeld';
import { BildungsBereich } from '../state/bildungsbereich.service';
import { ThemenfelderClient } from './themenfelder.client';

@Injectable({
  providedIn: 'root'
})
/**
 * Dadurch das die Logik der Themenfelder in einen Service ausgelagert ist und
 * der Service am Leben bleibt, auch wenn die Komponente zerstört wird,
 * kann man per Browser back zu den Themenfeldern navigieren und der vorherige Zustand ist wieder da.
 */
export class ThemenfelderService {
  private readonly themenfelderClient = inject(ThemenfelderClient);

  private readonly _ausgewaehlterBildungsbereich$ = new BehaviorSubject<BildungsBereich | null>(
    null
  );
  public ausgewaehlterBildungsbereich$ = this._ausgewaehlterBildungsbereich$.asObservable();
  private readonly _ausgewaehltesThemenfeld$ = new BehaviorSubject<Themenfeld | null>(null);
  public ausgewaehltesThemenfeld$ = this._ausgewaehltesThemenfeld$.asObservable();
  private readonly _ausgewaehlterSchwerpunkt$ = new BehaviorSubject<Themenfeld | null>(null);
  public ausgewaehlterSchwerpunkt$ = this._ausgewaehlterSchwerpunkt$.asObservable();

  public themenfelder$: Observable<Themenfeld[]> = this._ausgewaehlterBildungsbereich$.pipe(
    filter((bildungsbereich) => !!bildungsbereich),
    switchMap((bildungsbereich) => {
      return this.themenfelderClient.getThemenfelder(bildungsbereich);
    }),
    this.sort()
  );

  public themenfeldSchwerpunkte$: Observable<Themenfeld[]> = this._ausgewaehltesThemenfeld$.pipe(
    map((schwerpunkt) => schwerpunkt?.codenr),
    filter((codenr) => codenr != undefined),
    switchMap((codenr) => {
      return this.themenfelderClient.getThemenfeldSchwerpunkteByObercodenr(codenr);
    }),
    withLatestFrom(this._ausgewaehltesThemenfeld$),
    tap(([schwerpunkte, ausgewaehltesThemenfeld]) => {
      // Wenn keine Schwerpunkte vorhanden sind, lade direkt die Spezialisierungen
      if (schwerpunkte.length === 0) {
        this.onChangeSpezialisierung(ausgewaehltesThemenfeld);
      }
    }),
    map(([schwerpunkte]) => schwerpunkte),
    this.sort()
  );

  public themenfeldSpezialisierungen$: Observable<Themenfeld[]> =
    this._ausgewaehlterSchwerpunkt$.pipe(
      filter((spezialisierung) => !!spezialisierung),
      switchMap((spezialisierung) => {
        return this.themenfelderClient
          .getThemenfeldSpezialisierungenByObercodenr(spezialisierung?.codenr!)
          .pipe(
            map((themenfelder) => {
              // Es kann der Fall auftreten, dass des keine Spezialisierung zu dem gewählten Schwerpunkt gibt.
              // Sprich der DKZ knoten der spezialisierung hat keine kinder.
              // Damit man dennoch zur Suchseite ausspringen kann, wird die Spezialisierung einfach weiter gereicht
              // und dann als Link angezeigt.
              return themenfelder.length > 0 ? themenfelder : [spezialisierung!];
            })
          );
      }),
      this.sort()
    );

  onChangeBildungsbereich(bildungsbereich: BildungsBereich) {
    this._ausgewaehlterBildungsbereich$.next(bildungsbereich);
  }

  onChangeSchwerpunkt(themenfeld: Themenfeld | null) {
    this._ausgewaehltesThemenfeld$.next(themenfeld);
  }

  async onChangeSpezialisierung(themenfeld: Themenfeld | null) {
    this._ausgewaehlterSchwerpunkt$.next(themenfeld);
  }

  // NiceToKnow: Hier MUSS eine Arrow-Function verwendet werden.
  // Grund: Diese Funktion wird aus einem ng-template aufgerufen.
  // Eine "normale" funktion würde mit dem Scope des ng-template ausgeführt werden,
  // was dazu führt dass "this" undefined ist.
  // Arrow-Functions hingegen erben den Scope des Parents (hier ThemenfelderComponent).
  // Dieses Kommentar dient nur zum Wissensaustausch!
  fullReset = () => {
    this._ausgewaehlterBildungsbereich$.next(null);
    this._ausgewaehltesThemenfeld$.next(null);
    this._ausgewaehlterSchwerpunkt$.next(null);
  };

  resetSchwerpunkt = () => {
    this._ausgewaehltesThemenfeld$.next(null);
    this._ausgewaehlterSchwerpunkt$.next(null);
  };

  resetSpezialisierung = () => {
    this._ausgewaehlterSchwerpunkt$.next(null);
  };

  sort() {
    return map((arr: Themenfeld[]) =>
      arr.toSorted((ele1, ele2) => {
        if (ele1.kurzBezeichnungNeutral > ele2.kurzBezeichnungNeutral) {
          return 1;
        }
        if (ele1.kurzBezeichnungNeutral < ele2.kurzBezeichnungNeutral) {
          return -1;
        }
        return 0;
      })
    );
  }
}
